import React from "react";
import { Alert, Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

type VisitorConsentPageStatusProps = {
  messageKey: string;
  variant: "loading" | "error" | "success";
  logoUrl?: string | null;
};

export const VisitorConsentPageStatus: React.FC<VisitorConsentPageStatusProps> = ({
  messageKey,
  variant,
  logoUrl
}) => {
  const { t } = useTranslation();

  return (
    <Box
      data-testid="visitor-consent-page"
      sx={{
        minHeight: "100dvh",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "stretch",
        p: 0,
        bgcolor: "background.default"
      }}
    >
      <Paper
        elevation={2}
        sx={{
          width: "100%",
          maxWidth: { xs: "100%", md: 760 },
          minHeight: "100dvh",
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          borderRadius: { xs: 0, md: 2 }
        }}
        data-testid={`visitor-consent-page-status-${variant}`}
      >
        <Box
          sx={{
            m: { xs: 1, md: 1.5 },
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            flex: 1,
            bgcolor: "#ffffff"
          }}
        >
          <Stack
            spacing={2}
            alignItems="center"
            justifyContent="center"
            sx={{
              flex: 1,
              px: { xs: 2, md: 4 },
              py: { xs: 4, md: 5 },
              textAlign: "center"
            }}
          >
            {logoUrl ? (
              <Box
                component="img"
                src={logoUrl}
                alt="Company logo"
                data-testid="visitor-consent-company-logo"
                sx={{
                  maxWidth: { xs: 260, md: 320 },
                  maxHeight: { xs: 88, md: 112 },
                  width: "auto",
                  objectFit: "contain"
                }}
              />
            ) : null}
            {variant === "loading" ? (
              <CircularProgress data-testid="visitor-consent-page-loading" />
            ) : null}

            <Alert
              severity={variant === "success" ? "success" : variant === "error" ? "error" : "info"}
              sx={{ width: "100%", maxWidth: 560, textAlign: "left" }}
            >
              <Typography
                variant="body1"
                data-testid="visitor-consent-page-status-message"
                sx={{ wordBreak: "break-word" }}
              >
                {t(messageKey)}
              </Typography>
            </Alert>
          </Stack>
        </Box>
      </Paper>
    </Box>
  );
};
